import React from 'react'
import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {SerializedError} from '@reduxjs/toolkit'
import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query'
import {CountryType, MeasurementSystemType} from 'types/Localization'
import {WidgetLabelTypes} from 'utils/label'
import {HotelCarousel} from 'widgets/components/HotelCarousel'
import {SeeMoreButton} from 'widgets/components/SeeMoreButton'
import {MAXIMUM_HOTELS_TO_SHOW} from 'widgets/helpers/hotel'
import {SoldOut} from 'widgets/SoldOut'
import {WidgetContainer} from 'widgets/WidgetContainer'

import {Divider} from '@daedalus/atlas/Divider'
import {roomsToConfigurationObject} from '@daedalus/core/src/room/business/roomConfiguration'
import {BackendError} from '@daedalus/shared/src/booking/BackendError'

import {Hotel} from '../../../types/partnersApi/types'
import {HeaderSkeleton, WidgetHeader} from '../WidgetHeader'

interface MultipleHotelsListProps {
  isHideDividers?: boolean
  isDataLoaded?: boolean
  isSoldOut?: boolean
  isFetching?: boolean
  isLoading?: boolean
  error?: FetchBaseQueryError | SerializedError
  hotelsToShow: Hotel[]
  searchCheckIn?: string
  searchCheckOut?: string
  rooms: string
  taxDisplayLogic: CountryType
  isRtl?: boolean
  placeName?: string
  availableHotelsCount?: number
  currency: string
  ctaButtonUrl?: string
  onSupportButtonClick: () => void
  onReloadClick: () => void
  title?: string
  placeId?: string
  partnerKey: string
  label?: string
  distanceUnit: MeasurementSystemType
  searchDestination: string
}

const ErrorWrapper = styled('div')(
  ({theme}) => css`
    display: flex;
    min-height: 560px;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 0 ${theme.layout.spacing.s400}px;
  `
)

const SeeMoreButtonContainer = styled('div')(
  ({theme}) => css`
    padding-top: ${theme.layout.spacing.s400}px;
  `
)

const TopDivider = styled(Divider)(
  ({theme}) => css`
    margin-bottom: ${theme.layout.spacing.s500}px;
  `
)

const SoldOutWrapper = styled('div')(
  ({theme}) => css`
    display: flex;
    min-height: 271px;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 0 ${theme.layout.spacing.s400}px;
  `
)

export const MultipleHotelsList = ({
  searchCheckIn,
  searchCheckOut,
  currency,
  hotelsToShow,
  availableHotelsCount,
  placeName,
  isDataLoaded,
  isFetching,
  isHideDividers,
  isRtl,
  isSoldOut,
  rooms,
  taxDisplayLogic,
  ctaButtonUrl,
  error,
  isLoading,
  onReloadClick,
  onSupportButtonClick,
  title,
  placeId,
  partnerKey,
  label,
  distanceUnit,
  searchDestination
}: MultipleHotelsListProps) => {
  const {numberOfRooms} = roomsToConfigurationObject(rooms)
  // Show fallback text in case availableHotelsCount is bigger than amount hotel cards to show
  const isShowFallbackText = Boolean(
    availableHotelsCount &&
      availableHotelsCount < MAXIMUM_HOTELS_TO_SHOW &&
      hotelsToShow.length < availableHotelsCount
  )

  return (
    <>
      {!isHideDividers && <TopDivider />}

      <WidgetContainer
        borderType="none"
        height={588}
        partnerKey={partnerKey}
        label={label}
        widgetType={WidgetLabelTypes.multipleHotels}
      >
        {isDataLoaded && !isSoldOut && (
          <WidgetHeader
            placeName={placeName}
            checkIn={searchCheckIn}
            checkOut={searchCheckOut}
            rooms={rooms}
            title={title}
            placeId={placeId}
          />
        )}
        {(isFetching || isLoading) && <HeaderSkeleton />}
        <HotelCarousel
          isDataLoaded={isDataLoaded}
          isSoldOut={isSoldOut}
          taxDisplayLogic={taxDisplayLogic}
          currency={currency}
          partnerKey={partnerKey}
          label={label}
          distanceUnit={distanceUnit}
          numberOfRooms={numberOfRooms}
          searchCheckIn={searchCheckIn}
          searchCheckOut={searchCheckOut}
          searchDestination={searchDestination}
          hotelsToShow={hotelsToShow}
          isRtl={isRtl}
        />

        {isDataLoaded && isSoldOut && (
          <SoldOutWrapper>
            <SoldOut
              checkInDate={searchCheckIn}
              checkOutDate={searchCheckOut}
            />
          </SoldOutWrapper>
        )}

        {!!error && (
          <ErrorWrapper>
            <BackendError
              onReloadClick={onReloadClick}
              onSupportLinkClick={onSupportButtonClick}
            />
          </ErrorWrapper>
        )}
        <SeeMoreButtonContainer>
          <SeeMoreButton
            hotelsNumber={availableHotelsCount}
            redirectUrl={ctaButtonUrl}
            isShowFallbackText={isShowFallbackText}
            partnerKey={partnerKey}
            label={label}
            isSoldOut={isSoldOut}
            isDataLoaded={isDataLoaded}
          />
        </SeeMoreButtonContainer>
      </WidgetContainer>

      {!isHideDividers && <Divider />}
    </>
  )
}

export default MultipleHotelsList
